"use client";

import { tabSwitched } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Program Fee 1043 — BDU Fee Structure.
 *
 * Port of BDU v1 `components/Degree/FeePlan.tsx`: a heading over a pill switch for the
 * student type, the fee plans as a list on the left (a dropdown below 768px), a
 * line-item table with a highlighted total row, and a tinted disclaimer band.
 *
 * TWO GRADIENTS, and they are NOT the same colour or angle. The rule under the heading
 * is BDU's blue `bg-fee-header` — `linear-gradient(90deg, …)`, and it COLLAPSES to zero
 * height from 768px up. The selected student-type pill and the table header are its red
 * `bg-fee-button` — `linear-gradient(180deg, …)`, a vertical gradient.
 *
 * DATA — reuses the Program Fee 1 matrix verbatim, so nothing new is stored:
 *   fee type (tab)      = student type   — "Indian Students", "International Students"
 *   fee semester (col)  = fee plan       — "Semester Fee", "Annual Fee", "Lumpsum Fee"
 *   fee category (row)  = line item      — "Semester 1", "Year 1", "Lumpsum"
 *   fee value (cell)    = the amount
 * A line item with no value for the selected plan is skipped, so the plans can hold
 * different line items.
 *
 * TOTAL — BDU stores a `total_fee` alongside the line items; here the total is the SUM
 * of the visible amounts, so it cannot drift from the rows shown. Add no explicit
 * "Total" line item or it will be counted twice. The reference hides the detail rows
 * for single-line plans (its lumpsum case) and shows only the total; that falls out of
 * the same rule here.
 *
 * BREAKPOINTS — v1 `md:` (768px) is `tab:` here (this app remaps `md:` to 1230px).
 *
 * GRADIENT TEXT — `bg-clip-text` alone leaves the label INVISIBLE in WebKit because
 * this app's Lightning CSS pipeline emits no `-webkit-` prefixes, so
 * `WebkitBackgroundClip` is set inline as well.
 */

interface FeeTab {
  id: number;
  name: string;
  order_index?: number;
  is_active?: number | boolean;
  status?: number | boolean;
}

interface FeeRow {
  id: number;
  fee_type_id: number;
  name: string;
  order_index?: number;
  is_active?: number | boolean;
  status?: number | boolean;
}

interface FeeColumn {
  id: number;
  fee_type_id: number;
  name: string;
  semester_number?: number | null;
  order_index?: number;
  is_active?: number | boolean;
  status?: number | boolean;
}

interface FeeCell {
  id: number;
  fee_type_id: number;
  category_id: number;
  semester_id: number;
  value?: string;
  is_active?: number | boolean;
  status?: number | boolean;
}

interface ProgramFee1043Data {
  title?: string;
  title_text_color?: string;
  /** Full-bleed background art behind the section */
  section_background_image?: string;
  /** Disclaimer band (rich text) */
  other_fee?: string;
  /** Disclaimer band fill */
  background_color?: string;
  /** Blue rule under the heading (collapses from 768px up) */
  gradient_color_1?: string;
  gradient_color_2?: string;
  /** Red fill — selected pill + table header (vertical gradient) */
  gradient_color_3?: string;
  gradient_color_4?: string;
  /** Selected plan row + total row surface */
  table_header_background_color?: string;
  tabs?: FeeTab[];
  rows?: FeeRow[];
  columns?: FeeColumn[];
  values?: FeeCell[];
  /** The API also returns these under their domain names. */
  feeTypes?: FeeTab[];
  categories?: FeeRow[];
  semesters?: FeeColumn[];
  feeValues?: FeeCell[];
  status?: boolean | number;
}

/** The 24x25 caret the reference renders as `Arrow1` (grey) / `Arrow2` (gradient). */
const Caret = ({ gradientId }: { gradientId?: string }) => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    className="shrink-0"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.5604 11.2856C15.8413 11.5669 15.9991 11.9481 15.9991 12.3456C15.9991 12.7431 15.8413 13.1244 15.5604 13.4056L9.90438 19.0636C9.62298 19.3449 9.24138 19.5028 8.84352 19.5027C8.44567 19.5026 8.06414 19.3445 7.78288 19.0631C7.50162 18.7817 7.34366 18.4001 7.34375 18.0023C7.34384 17.6044 7.50198 17.2229 7.78338 16.9416L12.3794 12.3456L7.78338 7.74961C7.51 7.46684 7.35863 7.08801 7.36186 6.69471C7.36509 6.30141 7.52267 5.92512 7.80065 5.64687C8.07863 5.36863 8.45478 5.2107 8.84807 5.20709C9.24137 5.20349 9.62035 5.35451 9.90338 5.62761L15.5614 11.2846L15.5604 11.2856Z"
      fill={gradientId ? `url(#${gradientId})` : "#C3C3C3"}
    />
  </svg>
);

const live = <T extends { is_active?: number | boolean; status?: number | boolean }>(
  rows: T[] | undefined,
): T[] =>
  Array.isArray(rows)
    ? rows.filter((r) => r?.is_active !== 0 && r?.status !== 0)
    : [];

const byOrder = <T extends { order_index?: number }>(a: T, b: T) =>
  (Number(a.order_index) || 0) - (Number(b.order_index) || 0);

/** 91000 → "91,000" using the Indian digit grouping the reference uses. */
function formatIndian(value: string | number | undefined): string {
  const n = Number(String(value ?? "").replace(/,/g, "").trim());
  if (!Number.isFinite(n)) return String(value ?? "");
  return n.toLocaleString("en-IN");
}

function isBlank(html: string | undefined): boolean {
  return !String(html ?? "")
    .replace(/<[^>]*>/g, "")
    .trim();
}

const ProgramFee1043 = ({ data }: { data: any }) => {
  const sectionData = (data?.data ?? data) as ProgramFee1043Data | undefined;

  const {
    title = "Fee Structure",
    title_text_color = "#222023",
    section_background_image = "",
    other_fee = "",
    background_color = "#836537",
    gradient_color_1 = "#1F3D56",
    gradient_color_2 = "#7CB5E6",
    gradient_color_3 = "#F50113",
    gradient_color_4 = "#A30113",
    table_header_background_color = "#FFDCDE",
  } = sectionData || {};

  const tabs = useMemo(
    () => live(sectionData?.tabs ?? sectionData?.feeTypes).sort(byOrder),
    [sectionData?.tabs, sectionData?.feeTypes],
  );
  const allRows = useMemo(
    () => live(sectionData?.rows ?? sectionData?.categories).sort(byOrder),
    [sectionData?.rows, sectionData?.categories],
  );
  const allColumns = useMemo(
    () => live(sectionData?.columns ?? sectionData?.semesters).sort(byOrder),
    [sectionData?.columns, sectionData?.semesters],
  );
  const allValues = useMemo(
    () => live(sectionData?.values ?? sectionData?.feeValues),
    [sectionData?.values, sectionData?.feeValues],
  );

  const [selectedTabId, setSelectedTabId] = useState<number | null>(null);
  const [selectedColumnId, setSelectedColumnId] = useState<number | null>(null);
  const [planMenuOpen, setPlanMenuOpen] = useState(false);
  const planMenuRef = useRef<HTMLDivElement | null>(null);

  const activeTab = tabs.find((t) => t.id === selectedTabId) ?? tabs[0];

  const columns = useMemo(
    () => allColumns.filter((c) => c.fee_type_id === activeTab?.id),
    [allColumns, activeTab?.id],
  );

  const activeColumn =
    columns.find((c) => c.id === selectedColumnId) ?? columns[0];

  // The reference's mobile control is a Radix Accordion, which closes on outside click.
  useEffect(() => {
    if (!planMenuOpen) return undefined;
    const close = (e: MouseEvent) => {
      if (!planMenuRef.current?.contains(e.target as Node)) setPlanMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [planMenuOpen]);

  /** Line items that actually carry an amount for the selected plan. */
  const lines = useMemo(() => {
    if (!activeTab || !activeColumn) return [];
    return allRows
      .filter((r) => r.fee_type_id === activeTab.id)
      .map((row) => {
        const cell = allValues.find(
          (v) =>
            v.category_id === row.id &&
            v.semester_id === activeColumn.id &&
            v.fee_type_id === activeTab.id,
        );
        const raw = String(cell?.value ?? "").trim();
        return { id: row.id, name: row.name, raw };
      })
      .filter((line) => line.raw.length > 0);
  }, [allRows, allValues, activeTab, activeColumn]);

  const total = useMemo(
    () =>
      lines.reduce((sum, line) => {
        const n = Number(line.raw.replace(/[^0-9.-]/g, ""));
        return Number.isFinite(n) ? sum + n : sum;
      }, 0),
    [lines],
  );

  if (!sectionData?.status || tabs.length === 0 || !activeColumn) return null;

  const selectTab = (tab: FeeTab) => {
    setSelectedTabId(tab.id);
    // The new tab owns a different set of plans, so fall back to its first.
    setSelectedColumnId(null);
    tabSwitched({
      section: "program_fee_1043",
      component: "student_type",
      label: tab.name,
    });
  };

  const selectColumn = (column: FeeColumn) => {
    setSelectedColumnId(column.id);
    setPlanMenuOpen(false);
    tabSwitched({
      section: "program_fee_1043",
      component: "fee_plan",
      label: column.name,
    });
  };

  // `bg-fee-button` is a 180deg (vertical) gradient — not the 90deg one used elsewhere.
  const buttonGradient = `linear-gradient(180deg, ${gradient_color_3} 0%, ${gradient_color_4} 100%)`;
  const ruleGradient = `linear-gradient(90deg, ${gradient_color_1} 0%, ${gradient_color_2} 100%)`;
  const clipText = {
    backgroundImage: `linear-gradient(to right, ${gradient_color_3}, ${gradient_color_4})`,
    WebkitBackgroundClip: "text" as const,
    backgroundClip: "text" as const,
  };
  const showDetailRows = lines.length > 1;

  return (
    <section className="relative flex w-full items-center justify-center">
      <div
        className="flex w-full flex-col items-center justify-center gap-[42px] bg-cover bg-center py-[40px] tab:py-[60px]"
        style={
          section_background_image
            ? { backgroundImage: `url('${section_background_image}')` }
            : undefined
        }
      >
        <div className="flex w-full flex-col items-center justify-center gap-[43px]">
          <div className="flex flex-col items-center justify-center gap-[24px] tab:gap-[10px]">
            <h2
              className="flex flex-col items-center justify-center text-[24px] font-bold leading-[34px] tracking-normal tab:text-[28px] tab:leading-[100%]"
              style={{ color: title_text_color }}
            >
              {title}
              {/* The rule collapses to zero height from 768px up, as in the reference. */}
              <span
                className="h-[2px] w-[200px] tab:h-0"
                style={{ backgroundImage: ruleGradient }}
              />
            </h2>

            {/* Student-type pills */}
            <div className="flex cursor-pointer rounded-[40px] bg-white shadow-[0px_4px_15px_0px_rgba(0,0,0,0.25)]">
              {tabs.map((tab) => {
                const isActive = tab.id === activeTab?.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => selectTab(tab)}
                    className={cn(
                      "rounded-[64px] px-[18px] py-[12px] text-center align-middle text-[14px] font-medium leading-[18px] tracking-[-0.24px] tab:text-[16px] tab:leading-[20px]",
                      isActive && "text-white",
                    )}
                    style={
                      isActive ? { backgroundImage: buttonGradient } : undefined
                    }
                  >
                    {tab.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/*
            Below 768px the plan list is a dropdown, not a row of chips.

            IN FLOW, NOT AN OVERLAY. The reference is a Radix Accordion, so the open
            list PUSHES the fee card below it down rather than floating over it — the
            `gap-[16px]` here is the accordion item's own gap between trigger and
            content. Do not make this `absolute`; it would cover the card.
          */}
          <div
            ref={planMenuRef}
            className="flex w-[324px] flex-col gap-[16px] rounded-[4px] tab:hidden"
          >
            <button
              type="button"
              onClick={() => columns.length > 1 && setPlanMenuOpen((o) => !o)}
              aria-expanded={planMenuOpen}
              className="flex h-[36px] w-full items-center justify-between rounded-[4px] bg-white px-[8px] py-[6px] text-left font-semibold"
            >
              <span
                className="bg-clip-text text-[17px] font-medium leading-[24px] text-transparent"
                style={clipText}
              >
                {activeColumn.name}
              </span>
              <span className={cn(planMenuOpen && "block rotate-90")}>
                <Caret />
              </span>
            </button>
            {planMenuOpen && (
              <div className="w-full overflow-hidden rounded-[5px] bg-white">
                {columns.map((column) => (
                  <button
                    key={column.id}
                    type="button"
                    onClick={() => selectColumn(column)}
                    className={cn(
                      "flex h-[44px] w-full items-center pl-[8px] text-left text-[16px] font-medium leading-[23px] text-[#000000CC]",
                      column.id === activeColumn.id && "bg-[#F0F0F0]",
                    )}
                  >
                    {column.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex h-full gap-[48px] rounded-[9px] tab:min-h-[374px] tab:w-[788px] tab:border tab:border-[#B7B7B7] tab:bg-white tab:px-[22px] tab:py-[24px] tab:shadow-[0px_4px_29.4px_0px_#00000017]">
            {/* Fee-plan list (desktop) */}
            <div className="hidden tab:flex tab:w-[300px] tab:flex-col">
              {columns.map((column) => {
                const isActive = column.id === activeColumn.id;
                return (
                  <button
                    key={column.id}
                    type="button"
                    onClick={() => selectColumn(column)}
                    className={cn(
                      "flex cursor-pointer items-center justify-between rounded-[5px] tab:px-[10px] tab:py-[8px]",
                      isActive && "text-[#D90113]",
                    )}
                    style={
                      isActive
                        ? { backgroundColor: table_header_background_color }
                        : undefined
                    }
                  >
                    <span>{column.name}</span>
                    {isActive ? (
                      <>
                        <svg width="0" height="0" className="absolute" aria-hidden>
                          <defs>
                            <linearGradient
                              id={`fee-caret-${column.id}`}
                              x1="11.67"
                              y1="5.2"
                              x2="11.67"
                              y2="19.5"
                              gradientUnits="userSpaceOnUse"
                            >
                              <stop stopColor={gradient_color_3} />
                              <stop offset="1" stopColor={gradient_color_4} />
                            </linearGradient>
                          </defs>
                        </svg>
                        <Caret gradientId={`fee-caret-${column.id}`} />
                      </>
                    ) : (
                      <Caret />
                    )}
                  </button>
                );
              })}
            </div>
            <div className="hidden w-px bg-[#D9D9D9] tab:block" />

            <div className="flex w-full items-center justify-center tab:w-[320px] tab:items-stretch tab:justify-start">
              <table className="w-[320px] overflow-hidden rounded-[12px] bg-white text-white tab:w-[344px]">
                {showDetailRows && (
                  <thead className="tab:h-[54px]">
                    <tr
                      className="flex h-[48px] items-center justify-between px-[15px] tab:h-[54px] tab:px-[16px]"
                      style={{ backgroundImage: buttonGradient }}
                    >
                      <th className="text-[14px] font-medium tab:py-[2px] tab:text-[16px]">
                        {activeColumn.name}
                      </th>
                      <th className="text-[14px] font-medium tab:py-[2px] tab:text-[16px]">
                        Amount
                      </th>
                    </tr>
                  </thead>
                )}
                <tbody>
                  {showDetailRows &&
                    lines.map((line) => (
                      <tr
                        key={line.id}
                        className="flex h-[48px] items-center justify-between border border-[#DEDEDE] px-[15px] text-[#535252] tab:h-[54px] tab:px-[16px]"
                      >
                        <td>{line.name}</td>
                        <td>Rs. {formatIndian(line.raw)} /-</td>
                      </tr>
                    ))}
                  <tr
                    className="flex h-[48px] items-center justify-between rounded-b-[9px] px-[15px] text-[#D90113] tab:h-[54px] tab:px-[16px]"
                    style={{ backgroundColor: table_header_background_color }}
                  >
                    <td>Total fees</td>
                    <td>Rs. {formatIndian(total)} /-</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {!isBlank(other_fee) && (
            <div className="flex w-full items-center justify-center tab:px-[64px]">
              {/*
                Deliberately NOT `.rich-text-content`. That helper forces
                `p { font-weight: … !important }` and its own heading/list scale, which
                overrides the `font-bold` the reference relies on here and adds bullets
                the reference does not have. BDU renders this markup raw inside a
                bold/white wrapper and lets preflight flatten the rest, so this does too.
              */}
              <div
                className="flex w-[314px] flex-col justify-center rounded-[5px] p-[20px] font-bold text-white tab:w-full tab:px-[40px] tab:text-[16px] tab:leading-[22px]"
                style={{ backgroundColor: background_color }}
              >
                <div dangerouslySetInnerHTML={{ __html: other_fee }} />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ProgramFee1043;
